/**
 * Copyright 2026 Bluecircle Technologies Pvt Limited. All rights reserved.
 *
 * This software and its source code are the proprietary and confidential
 * intellectual property of Bluecircle Technologies Pvt Limited. Unauthorized
 * copying, reproduction, distribution, modification, public display, or use
 * of this software, in whole or in part, is strictly prohibited without the
 * prior written permission of Bluecircle Technologies Pvt Limited.
 *
 * Portions derived from Grist (Copyright Grist Labs), used under the Apache
 * License, Version 2.0. See the NOTICE file for details.
 */

import { loadUserManager } from "app/client/lib/imports";
import { AppModel } from "app/client/models/AppModel";
import { FullUser, Organization, UserAPI } from "app/common/UserAPI";

export interface ManageTeamUsersOptions {
  org: Organization;
  user: FullUser | null;
  api: UserAPI;
  onSave?: (personal: boolean) => Promise<unknown>;
}

// Opens the user-manager for the given org.
export async function manageTeamUsers({ org, user, api, onSave }: ManageTeamUsersOptions) {
  (await loadUserManager()).showUserManagerModal(api, {
    permissionData: api.getOrgAccess(org.id),
    activeUser: user,
    resourceType: "organization",
    resourceId: org.id,
    resource: org,
    onSave,
  });
}

export interface ManagePersonalUsersAppOptions {
  app: AppModel;
  onSave?: (personal: boolean) => Promise<unknown>;
}

// Opens the user-manager for the current org in the given AppModel.
export async function manageTeamUsersApp({ app, onSave }: ManagePersonalUsersAppOptions) {
  if (app.currentOrg) {
    return manageTeamUsers({ org: app.currentOrg, user: app.currentValidUser, api: app.api, onSave });
  }
}

export interface ManageWorkspaceUsersOptions {
  workspace: any;
  user: FullUser | null;
  api: UserAPI;
  onSave?: (personal: boolean) => Promise<unknown>;
}

export async function manageWorkspaceUsers({ workspace, user, api, onSave }: ManageWorkspaceUsersOptions) {
  (await loadUserManager()).showUserManagerModal(api, {
    permissionData: api.getWorkspaceAccess(workspace.id),
    activeUser: user,
    resourceType: "workspace",
    resourceId: workspace.id,
    resource: workspace,
    onSave,
  });
}

export interface ManageDocUsersOptions {
  doc: any;
  user: FullUser | null;
  api: UserAPI;
  onSave?: (personal: boolean) => Promise<unknown>;
}

export async function manageDocUsers({ doc, user, api, onSave }: ManageDocUsersOptions) {
  (await loadUserManager()).showUserManagerModal(api, {
    permissionData: api.getDocAccess(doc.id),
    activeUser: user,
    resourceType: "document",
    resourceId: doc.id,
    resource: doc,
    onSave,
  });
}
